/**
 * App.js - Main application with routing
 * 
 * Refactored to import page components from /pages folder
 * This file now only handles routing and top-level providers
 */
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Loader2, Check, Lock, Mail, ArrowRight, Shield, Heart, Sparkles,
  ArrowLeft, CheckCircle, Clock, User, FileText, Download, AlertCircle,
  MessageCircle, ChevronDown, ChevronUp, Lightbulb, X
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import './App.css';

// Import page components
import {
  PlansPage,
  StartPage,
  OnboardingPage,
  CheckoutPage,
  CheckoutSuccessPage,
  IntakePage,
  InvitePage,
  ReflectionPage,
  ReviewPage,
  LockPage,
  PartnerJoinPage,
  SignaturePage,
  SigningEntryPage,
  WaitingPage,
  CertificatePage,
  UpgradePage,
  RevealPage,
  SharedRevealPage,
  TestCertificatePage,
  AlignmentIntelligencePage,
  ResultsHubPage
} from './pages';

// Import shared components
import Header from './components/shared/Header';
import InsightsPanel from './components/shared/InsightsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// V2 API toggle - enable to use new v2 endpoints for Reveal and state
const USE_V2_API = process.env.REACT_APP_USE_V2_API === 'true';
const API_V2 = `${BACKEND_URL}/api/v2`;

// Helper to get the right API base for specific endpoints
const getApiUrl = (endpoint, useV2 = false) => {
  if (useV2 && USE_V2_API) {
    return `${API_V2}${endpoint}`;
  }
  return `${API}${endpoint}`;
};

// Helper for consistent timezone-aware date formatting
const formatDateTime = (isoString, options = {}) => {
  if (!isoString) return '—';
  try {
    const date = new Date(isoString);
    const defaultOptions = {
      dateStyle: 'long',
      timeStyle: 'short',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, // User's timezone
      ...options
    };
    return new Intl.DateTimeFormat('en-US', defaultOptions).format(date);
  } catch {
    return isoString;
  }
};

// Helper for date only (no time)
const formatDate = (isoString) => {
  if (!isoString) return '—';
  try {
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'long',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }).format(date);
  } catch {
    return isoString;
  }
};

// ==================== DASHBOARD PAGE ====================
const DashboardPage = () => {
  const navigate = useNavigate();
  const [relationship, setRelationship] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRelationship();
  }, []);

  const fetchRelationship = async () => {
    try {
      const relationshipId = sessionStorage.getItem('relationship_id');
      
      // Use V2 API for relationship state (better role validation)
      const url = USE_V2_API 
        ? `${API_V2}/relationships/${relationshipId}?debug=true`
        : `${API}/relationships/${relationshipId}`;
      
      console.log(`[API] Using ${USE_V2_API ? 'V2' : 'V1'} relationship endpoint`);
      const response = await axios.get(url);
      setRelationship(response.data);
      
      // Auto-route based on state
      const state = response.data.state;
      console.log('[DASHBOARD] Relationship state:', state);
      
      // Log role validation in v2
      if (USE_V2_API && state?.roles_valid !== undefined) {
        console.log('[DASHBOARD] Role validation:', state.roles_valid);
      }
      
      // If certificate is ready, redirect to certificate page
      const completedStatuses = ['CERT_READY', 'COMPLETED'];
      if (completedStatuses.includes(state?.status) || state?.certificate_ready) {
        navigate('/certificate');
        return;
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching relationship:', error);
      toast.error('Failed to load dashboard');
      setLoading(false);
    }
  };

  const handleStartReflection = () => {
    navigate('/reflection');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center" style={{minHeight: 'calc(100vh - 100px)'}}>
          <Loader2 className="w-8 h-8 animate-spin text-stone-900" />
        </div>
      </div>
    );
  }

  const currentUserId = sessionStorage.getItem('user_id');
  const currentPartner = relationship?.partners?.find(p => p.user_id === currentUserId);
  const otherPartner = relationship?.partners?.find(p => p.user_id !== currentUserId);
  const state = relationship?.state || {};

  // Determine status display - more accurate based on actual state
  const getStatusDisplay = (partner, isCurrent) => {
    if (partner?.signed_at) {
      return { icon: CheckCircle, text: 'Signed', color: 'text-mc-blue-deep' };
    }
    if (partner?.locked_at) {
      return { icon: Lock, text: 'Responses Locked', color: 'text-mc-blue-deep' };
    }
    // Check if partner has started (has any responses)
    // For now, use invite_status or accepted_at as proxy
    if (partner?.accepted_at || partner?.invite_status === 'accepted') {
      if (isCurrent) {
        return { icon: Clock, text: 'In Progress', color: 'text-blue-600' };
      }
      return { icon: Clock, text: 'In Progress', color: 'text-blue-600' };
    }
    // Partner hasn't accepted/joined yet
    if (partner?.invite_status === 'sent') {
      return { icon: Mail, text: 'Invitation Sent', color: 'text-amber-600' };
    }
    if (isCurrent) {
      return { icon: Clock, text: 'Not Started', color: 'text-stone-400' };
    }
    return { icon: Clock, text: 'Waiting to Join', color: 'text-stone-400' };
  };

  const currentStatus = getStatusDisplay(currentPartner, true);
  const otherStatus = getStatusDisplay(otherPartner, false);

  // Determine next action
  const getNextAction = () => {
    // If certificate ready, show view certificate
    if (state.certificate_ready || state.status === 'CERT_READY' || state.status === 'COMPLETED') {
      return (
        <button
          onClick={() => navigate('/certificate')}
          className="bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] inline-flex items-center gap-2"
          data-testid="view-certificate"
        >
          View Your Certificate
          <CheckCircle className="w-5 h-5" />
        </button>
      );
    }

    // If waiting for partner to sign
    if (currentPartner?.signed_at && !otherPartner?.signed_at) {
      return (
        <div className="text-stone-600">
          <p className="mb-4">You've signed! Waiting for your partner to sign...</p>
          <button
            onClick={() => navigate('/waiting')}
            className="bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] inline-flex items-center gap-2"
          >
            Check Partner Status
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      );
    }

    // If both locked but not signed
    if (state.both_locked && !currentPartner?.signed_at) {
      return (
        <button
          onClick={() => navigate('/reveal')}
          className="bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] inline-flex items-center gap-2"
        >
          View Shared Summary & Sign
          <ArrowRight className="w-5 h-5" />
        </button>
      );
    }

    // If current user hasn't locked
    if (!currentPartner?.locked_at) {
      return (
        <button
          onClick={handleStartReflection}
          className="bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] inline-flex items-center gap-2"
          data-testid="start-reflection"
        >
          Start Your Reflection
          <ArrowRight className="w-5 h-5" />
        </button>
      );
    }

    // Waiting for partner to lock
    return (
      <div className="text-stone-600">
        <p className="mb-4">Your responses are locked. Waiting for your partner to complete their reflection.</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 mb-2 break-words">Your MarrsingClause® Dashboard</h2>
          <p className="text-sm sm:text-base text-stone-600 mb-4">Track your progress and partner status</p>
          
          {/* Status badge */}
          <div className="mb-8 sm:mb-12">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-stone-100 text-stone-700">
              Status: {state.status || 'IN_PROGRESS'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-12">
            {/* Your Status */}
            <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5 sm:p-8">
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <User className="w-5 h-5 sm:w-6 sm:h-6 text-stone-900 flex-shrink-0" />
                <h3 className="text-lg sm:text-xl font-semibold text-stone-900">Your Status</h3>
              </div>
              <p className="text-xs sm:text-sm text-stone-500 mb-3 sm:mb-4">Partner {currentPartner?.role || 'A'}</p>
              
              <div className={`flex items-center gap-2 ${currentStatus.color}`}>
                <currentStatus.icon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                <span className="text-sm sm:text-base font-medium">{currentStatus.text}</span>
              </div>
            </div>

            {/* Partner Status */}
            <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5 sm:p-8">
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <User className="w-5 h-5 sm:w-6 sm:h-6 text-stone-900 flex-shrink-0" />
                <h3 className="text-lg sm:text-xl font-semibold text-stone-900">Partner Status</h3>
              </div>
              <p className="text-xs sm:text-sm text-stone-500 mb-3 sm:mb-4">Partner {otherPartner?.role || 'B'}</p>
              
              <div className={`flex items-center gap-2 ${otherStatus.color}`}>
                <otherStatus.icon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                <span className="text-sm sm:text-base font-medium">{otherStatus.text}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-stone-50 rounded-2xl border border-stone-100 p-8">
            <h3 className="text-xl font-semibold text-stone-900 mb-4">Next Steps</h3>
            {getNextAction()}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== APP ====================
export default function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<PlansPage />} />
          <Route path="/start" element={<StartPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/intake" element={<IntakePage />} />
          <Route path="/invite" element={<InvitePage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/reflection" element={<ReflectionPage />} />
          <Route path="/review" element={<ReviewPage />} />
          <Route path="/lock" element={<LockPage />} />
          <Route path="/partner/join" element={<PartnerJoinPage />} />
          <Route path="/reveal" element={<SharedRevealPage />} />
          <Route path="/reveal/:relationshipId" element={<RevealPage />} />
          <Route path="/signature" element={<SignaturePage />} />
          <Route path="/sign" element={<SigningEntryPage />} />
          <Route path="/waiting" element={<WaitingPage />} />
          <Route path="/certificate" element={<CertificatePage />} />
          <Route path="/certificate/:relationshipId" element={<CertificatePage />} />
          <Route path="/results/:relationshipId" element={<ResultsHubPage />} />
          <Route path="/alignment-intelligence" element={<AlignmentIntelligencePage />} />
          <Route path="/alignment-intelligence/:relationshipId" element={<AlignmentIntelligencePage />} />
          <Route path="/test-certificate" element={<TestCertificatePage />} />
          <Route path="/upgrade" element={<UpgradePage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}
